import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { getImageUrl } from '../utils/imageUtils';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Avatar,
  Button,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Divider,
  useTheme,
  alpha,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  TextField,
  InputAdornment,
  Badge,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormControl,
  InputLabel,
  OutlinedInput,
} from '@mui/material';
import {
  Edit as EditIcon,
  PhotoCamera as PhotoCameraIcon,
  LocationOn as LocationIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  Event as EventIcon,
  People as PeopleIcon,
  Star as StarIcon,
  Favorite as FavoriteIcon,
  Share as ShareIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  CalendarToday as CalendarIcon,
  AddPhotoAlternate as AddPhotoIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  AccountCircle as AccountCircleIcon,
  Wallpaper as WallpaperIcon,
} from '@mui/icons-material';
import {
  getOrganizerProfile,
  getOrganizerEvents,
  getPublicOrganizerProfile,
  getPublicOrganizerEvents,
  uploadGalleryImage,
  deleteGalleryImage,
  updateOrganizerSocialMedia,
  updateOrganizerProfile,
} from '../services/eventService';

const OrganizerProfile = () => {
  const theme = useTheme();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { organizerId } = useParams();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageCaption, setImageCaption] = useState('');
  const [uploadProgress, setUploadProgress] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [socialMediaDialogOpen, setSocialMediaDialogOpen] = useState(false);
  const [socialMediaLinks, setSocialMediaLinks] = useState({
    facebook: '',
    twitter: '',
    instagram: '',
    linkedin: '',
  });
  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const profileInputRef = useRef(null);
  const isPublicView = !!organizerId;

  // Fetch organizer profile and events
  const { data: profile, isLoading: profileLoading, error: profileError } = useQuery(
    ['organizerProfile', organizerId],
    () => isPublicView ? getOrganizerProfile() : getOrganizerProfile(),
    {
      onError: (error) => {
        if (error.response?.status === 404) {
          history.push('/create-profile');
        }
      },
    }
  );

  const { data: events, isLoading: eventsLoading } = useQuery(
    ['organizerEvents', organizerId],
    () => getOrganizerEvents(),
    {
      enabled: !!profile,
    }
  );

  // Calculate stats from real data (must be before early returns)
  const stats = useMemo(() => {
    if (!events) return { totalEvents: 0, upcomingEvents: 0, totalVendors: 0, totalApplications: 0 };
    
    const allApplications = events.reduce((acc, event) => acc + (event.application_count || 0), 0);
    
    return {
      totalEvents: events.length,
      upcomingEvents: events.filter(event => new Date(event.date) > new Date()).length,
      totalVendors: allApplications, // Approximation
      totalApplications: allApplications,
    };
  }, [events]);

  // Enforce private access: if URL id is present and doesn't match current user's organizer id, redirect to own
  useEffect(() => {
    if (!profile || !organizerId) return;
    const urlId = Number(organizerId);
    if (Number.isFinite(urlId) && profile.organizer_id && urlId !== profile.organizer_id) {
      history.replace(`/organizers/${profile.organizer_id}`);
    }
  }, [profile, organizerId, history]);

  // Upload image mutation
  const uploadImageMutation = useMutation(
    (data) => uploadGalleryImage(data.image, data.caption),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('organizerProfile');
        setUploadDialogOpen(false);
        setSelectedImage(null);
        setImageCaption('');
        setUploadProgress(false);
        setSnackbar({
          open: true,
          message: 'Image uploaded successfully!',
          severity: 'success',
        });
      },
      onError: (error) => {
        setUploadProgress(false);
        setSnackbar({
          open: true,
          message: `Error uploading image: ${error.message}`,
          severity: 'error',
        });
      },
    }
  );

  // Delete image mutation
  const deleteImageMutation = useMutation(
    (imageId) => deleteGalleryImage(imageId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('organizerProfile');
        setSnackbar({
          open: true,
          message: 'Image deleted successfully!',
          severity: 'success',
        });
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: `Error deleting image: ${error.message}`,
          severity: 'error',
        });
      },
    }
  );

  // Update social media mutation
  const updateSocialMediaMutation = useMutation(
    (data) => updateOrganizerSocialMedia(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('organizerProfile');
        setSocialMediaDialogOpen(false);
        setSnackbar({
          open: true,
          message: 'Social media links updated successfully!',
          severity: 'success',
        });
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: `Error updating social media: ${error.message}`,
          severity: 'error',
        });
      },
    }
  );

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleEditProfile = () => {
    history.push('/create-organizer-profile');
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
      setUploadDialogOpen(true);
    }
  };

  const handleUploadConfirm = () => {
    if (selectedImage) {
      setUploadProgress(true);
      uploadImageMutation.mutate({
        image: selectedImage,
        caption: imageCaption,
      });
      }
    };

  const handleDeleteImage = (imageId) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      deleteImageMutation.mutate(imageId);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSocialMediaEdit = () => {
    // Initialize social media links from profile data
    setSocialMediaLinks({
      facebook: profile?.facebook || '',
      twitter: profile?.twitter || '',
      instagram: profile?.instagram || '',
      linkedin: profile?.linkedin || '',
    });
    setSocialMediaDialogOpen(true);
  };

  const handleSocialMediaChange = (platform) => (event) => {
    setSocialMediaLinks({
      ...socialMediaLinks,
      [platform]: event.target.value,
    });
  };

  const handleSocialMediaSave = () => {
    updateSocialMediaMutation.mutate(socialMediaLinks);
  };

  const handleCoverPhotoChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        setUploadProgress(true);
        const formData = new FormData();
        formData.append('cover_photo', file);
        await updateOrganizerProfile(formData);
        queryClient.invalidateQueries(['organizerProfile']);
        queryClient.invalidateQueries(['organizerProfile', organizerId || 'current']);
        setSnackbar({
          open: true,
          message: 'Cover photo updated successfully!',
          severity: 'success',
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: `Error uploading cover photo: ${error.response?.data?.detail || error.message || 'Failed to upload cover photo'}`,
          severity: 'error',
        });
      } finally {
        setUploadProgress(false);
        if (event.target) {
          event.target.value = '';
        }
      }
    }
  };

  const handleProfilePictureChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        setUploadProgress(true);
        const formData = new FormData();
        formData.append('profile_picture', file);
        await updateOrganizerProfile(formData);
        queryClient.invalidateQueries(['organizerProfile']);
        queryClient.invalidateQueries(['organizerProfile', organizerId || 'current']);
        setSnackbar({
          open: true,
          message: 'Profile picture updated successfully!',
          severity: 'success',
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: `Error uploading profile picture: ${error.response?.data?.detail || error.message || 'Failed to upload profile picture'}`,
          severity: 'error',
        });
      } finally {
        setUploadProgress(false);
        if (event.target) {
          event.target.value = '';
        }
      }
    }
  };

  if (profileLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (profileError) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          {profileError.response?.status === 404
            ? 'Profile not found'
            : 'Error loading profile'}
        </Alert>
        {!isPublicView && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => history.push('/create-profile')}
            >
              Create Profile
            </Button>
        </Box>
        )}
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info">
          No profile found. Please create your organizer profile.
        </Alert>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={() => history.push('/create-organizer-profile')}
          >
            Create Profile
          </Button>
        </Box>
      </Container>
    );
  }

  console.log('Current profile data:', profile); // Debug log

  return (
    <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        {/* Modern Header with Cover Photo */}
        <Paper
          elevation={0} 
          sx={{ 
            position: 'relative',
            mb: 3,
            overflow: 'hidden',
            borderRadius: 3,
          }}
        >
          <Box
            sx={{
              height: 280,
              background: profile?.cover_photo_url 
                ? `url(${getImageUrl(profile.cover_photo_url)}) center/cover`
                : 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: profile?.cover_photo_url ? alpha('#000', 0.3) : 'transparent',
              }
            }}
          >
            {!isPublicView && (
              <IconButton
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  right: 16,
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                }}
                onClick={() => coverInputRef.current?.click()}
                disabled={uploadProgress}
              >
                <WallpaperIcon />
              </IconButton>
            )}
          </Box>
          <input
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            ref={coverInputRef}
            onChange={handleCoverPhotoChange}
          />
          
          {/* Profile Info Section */}
          <Box sx={{ p: 4, position: 'relative', bgcolor: 'white', pt: { xs: 8, md: 6 } }}>
            {/* Profile Picture - Positioned to overlap with cover photo */}
            <Box 
              sx={{ 
                position: 'relative', 
                display: 'inline-block', 
                mb: 3,
                mt: { xs: -8, md: -10 },
              }}
            >
              <Avatar
                src={profile?.profile_picture_url ? getImageUrl(profile.profile_picture_url) : undefined}
                sx={{
                  width: { xs: 120, md: 150 },
                  height: { xs: 120, md: 150 },
                  border: 4,
                  borderColor: 'white',
                  boxShadow: 3,
                }}
              >
                <BusinessIcon sx={{ fontSize: 60 }} />
              </Avatar>
              {!isPublicView && (
                <IconButton
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                    border: 3,
                    borderColor: 'white',
                  }}
                  onClick={() => profileInputRef.current?.click()}
                  disabled={uploadProgress}
                >
                  <AccountCircleIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              ref={profileInputRef}
              onChange={handleProfilePictureChange}
            />
            
            {/* Content Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
                  {profile?.organization_name || profile?.name || 'Organization Name'}
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 2, maxWidth: 600 }}>
                  {profile?.bio || 'No description available. Click Edit Profile to add information about your organization.'}
                </Typography>
                
                {/* Contact Info */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                  {profile?.contact_info && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {profile.contact_info}
                      </Typography>
                    </Box>
                  )}
                  {profile?.location && (
                    <Chip
                      icon={<LocationIcon />}
                      label={profile.location}
                      variant="outlined"
                      size="small"
                    />
                  )}
                  {profile?.website && (
                    <Chip
                      icon={<WebsiteIcon />}
                      label="Website"
                      variant="outlined"
                      size="small"
                      onClick={() => window.open(profile.website, '_blank')}
                      sx={{ cursor: 'pointer' }}
                    />
                  )}
                </Box>
                
                {/* Social Media Links - Button list with text */}
                {profile?.social_media && typeof profile.social_media === 'object' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.0, mb: 2, maxWidth: 420 }}>
                    {profile.social_media.facebook && (
                      <Button
                        variant="outlined"
                        startIcon={<FacebookIcon />}
                        onClick={() => {
                          const url = profile.social_media.facebook;
                          const formatted = /^(https?:)?\/\//.test(url) ? url : `https://${url}`;
                          window.open(formatted, '_blank', 'noopener,noreferrer');
                        }}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', borderRadius: 2, px: 1.5, py: 0.75, borderColor: alpha('#1877F2', 0.3) }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1877F2' }}>Facebook</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {profile.social_media.facebook.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                          </Typography>
                        </Box>
                      </Button>
                    )}
                    {profile.social_media.twitter && (
                      <Button
                        variant="outlined"
                        startIcon={<TwitterIcon />}
                        onClick={() => {
                          const url = profile.social_media.twitter;
                          const formatted = /^(https?:)?\/\//.test(url) ? url : `https://${url}`;
                          window.open(formatted, '_blank', 'noopener,noreferrer');
                        }}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', borderRadius: 2, px: 1.5, py: 0.75, borderColor: alpha('#1DA1F2', 0.3) }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1DA1F2' }}>Twitter</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {profile.social_media.twitter.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                          </Typography>
                        </Box>
                      </Button>
                    )}
                    {profile.social_media.instagram && (
                      <Button
                        variant="outlined"
                        startIcon={<InstagramIcon sx={{ color: '#E4405F' }} />}
                        onClick={() => {
                          const url = profile.social_media.instagram;
                          const formatted = /^(https?:)?\/\//.test(url) ? url : `https://${url}`;
                          window.open(formatted, '_blank', 'noopener,noreferrer');
                        }}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', borderRadius: 2, px: 1.5, py: 0.75, borderColor: alpha('#E4405F', 0.3) }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#E4405F' }}>Instagram</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {profile.social_media.instagram.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                          </Typography>
                        </Box>
                      </Button>
                    )}
                    {profile.social_media.linkedin && (
                      <Button
                        variant="outlined"
                        startIcon={<LinkedInIcon sx={{ color: '#0a66c2' }} />}
                        onClick={() => {
                          const url = profile.social_media.linkedin;
                          const formatted = /^(https?:)?\/\//.test(url) ? url : `https://${url}`;
                          window.open(formatted, '_blank', 'noopener,noreferrer');
                        }}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', borderRadius: 2, px: 1.5, py: 0.75, borderColor: alpha('#0a66c2', 0.3) }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#0a66c2' }}>LinkedIn</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {profile.social_media.linkedin.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                          </Typography>
                        </Box>
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
                  
              {/* Action Buttons */}
              {!isPublicView && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button 
                    variant="contained" 
                    startIcon={<EditIcon />}
                    onClick={handleEditProfile}
                    size="large"
                  >
                    Edit Profile
                  </Button>
                  <Button 
                    variant="outlined" 
                    startIcon={<ShareIcon />}
                    size="large"
                  >
                    Share
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Organizer Metrics - subdued cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={1} sx={{ bgcolor: 'white', height: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {stats.totalEvents}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.875rem' }}>
                      Events
                    </Typography>
                  </Box>
                  <EventIcon sx={{ fontSize: 36, color: alpha('#1976d2', 0.2) }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={1} sx={{ bgcolor: 'white', height: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {stats.upcomingEvents}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.875rem' }}>
                      Upcoming
                    </Typography>
                  </Box>
                  <CalendarIcon sx={{ fontSize: 36, color: alpha('#2e7d32', 0.2) }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={1} sx={{ bgcolor: 'white', height: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {stats.totalApplications}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.875rem' }}>
                      Applications
                    </Typography>
                  </Box>
                  <PeopleIcon sx={{ fontSize: 36, color: alpha('#0288d1', 0.2) }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={1} sx={{ bgcolor: 'white', height: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {stats.totalVendors}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.875rem' }}>
                      Vendor Partners
                    </Typography>
                  </Box>
                  <BusinessIcon sx={{ fontSize: 36, color: alpha('#ed6c02', 0.2) }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Quick Actions */}
        <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: 'white' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => history.push('/create-event')}
              size="large"
            >
              Create New Event
            </Button>
            <Button
              variant="outlined"
              startIcon={<EventIcon />}
              onClick={() => history.push('/my-events')}
              size="large"
            >
              View All Events
            </Button>
            <Button
              variant="outlined"
              startIcon={<PeopleIcon />}
              onClick={() => {
                // Navigate to first event's CRM or create a general CRM page
                if (events && events.length > 0) {
                  history.push(`/event/${events[0].event_id}/applications`);
                }
              }}
              size="large"
              disabled={!events || events.length === 0}
            >
              Manage Applications
            </Button>
          </Box>
        </Paper>

        {/* Main Content Tabs */}
        <Paper elevation={2} sx={{ mb: 3, bgcolor: 'white' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="About" />
          <Tab label="Events" />
          <Tab label="Gallery" />
          <Tab label="Reviews" />
        </Tabs>

        {/* About Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card elevation={0} sx={{ bgcolor: '#f9fafb', mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                      About Organization
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
                      {profile?.bio || profile?.about || 'No description available. Click "Edit Profile" to add information about your organization, mission, and values.'}
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card elevation={0} sx={{ bgcolor: '#f9fafb' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                      Contact Information
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {profile?.contact_info && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 1 }}>
                            <EmailIcon color="primary" />
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Contact</Typography>
                            <Typography variant="body1">{profile.contact_info}</Typography>
                          </Box>
                        </Box>
                      )}
                      {profile?.location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 1 }}>
                            <LocationIcon color="primary" />
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Location</Typography>
                            <Typography variant="body1">{profile.location}</Typography>
                          </Box>
                        </Box>
                      )}
                      {profile?.website && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 1 }}>
                            <WebsiteIcon color="primary" />
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Website</Typography>
                            <Button
                              variant="text"
                              onClick={() => window.open(profile.website, '_blank')}
                              sx={{ p: 0, textTransform: 'none' }}
                            >
                              {profile.website}
                            </Button>
                          </Box>
                        </Box>
                      )}
                      {!profile?.contact_info && !profile?.location && !profile?.website && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No contact information available. Edit your profile to add contact details.
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card elevation={0} sx={{ bgcolor: '#f9fafb' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Social Media
                      </Typography>
                      <IconButton 
                        color="primary" 
                        onClick={handleSocialMediaEdit}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {profile?.social_media && typeof profile.social_media === 'object' ? (
                        <>
                          {profile.social_media.facebook && (
                            <Button
                              startIcon={<FacebookIcon />}
                              variant="outlined"
                              fullWidth
                              onClick={() => window.open(profile.social_media.facebook, '_blank')}
                              sx={{ justifyContent: 'flex-start' }}
                            >
                              Facebook
                            </Button>
                          )}
                          {profile.social_media.twitter && (
                            <Button
                              startIcon={<TwitterIcon />}
                              variant="outlined"
                              fullWidth
                              onClick={() => window.open(profile.social_media.twitter, '_blank')}
                              sx={{ justifyContent: 'flex-start' }}
                            >
                              Twitter
                            </Button>
                          )}
                          {profile.social_media.instagram && (
                            <Button
                              startIcon={<InstagramIcon />}
                              variant="outlined"
                              fullWidth
                              onClick={() => window.open(profile.social_media.instagram, '_blank')}
                              sx={{ justifyContent: 'flex-start' }}
                            >
                              Instagram
                            </Button>
                          )}
                          {profile.social_media.linkedin && (
                            <Button
                              startIcon={<LinkedInIcon />}
                              variant="outlined"
                              fullWidth
                              onClick={() => window.open(profile.social_media.linkedin, '_blank')}
                              sx={{ justifyContent: 'flex-start' }}
                            >
                              LinkedIn
                            </Button>
                          )}
                        </>
                      ) : null}
                      {(!profile?.social_media || 
                        (typeof profile.social_media === 'object' && 
                         !profile.social_media.facebook && 
                         !profile.social_media.twitter && 
                         !profile.social_media.instagram && 
                         !profile.social_media.linkedin)) && (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            No social media links added yet
                          </Typography>
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleSocialMediaEdit}
                            sx={{ mt: 1 }}
                          >
                            Add Social Media
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Events Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 4 }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Your Events ({events?.length || 0})
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  size="small"
                  placeholder="Search events..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ bgcolor: '#f9fafb' }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => history.push('/create-event')}
                >
                  Create Event
                </Button>
              </Box>
            </Box>
            
            {eventsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : events && events.length > 0 ? (
              <Grid container spacing={3}>
                {events.filter(event => 
                  !searchQuery || 
                  event.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  event.location?.toLowerCase().includes(searchQuery.toLowerCase())
                ).map((event) => (
                  <Grid item xs={12} md={6} lg={4} key={event.event_id}>
                    <Card 
                      elevation={2}
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: 6,
                          transform: 'translateY(-4px)',
                          transition: 'all 0.3s ease',
                        }
                      }}
                      onClick={() => history.push(`/event/${event.event_id}`)}
                    >
                      <CardMedia
                        component="img"
                        height="180"
                        image={event.image_url ? getImageUrl(event.image_url) : 'https://via.placeholder.com/400x200?text=Event+Image'}
                        alt={event.title}
                      />
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" gutterBottom noWrap sx={{ fontWeight: 'bold' }}>
                          {event.title}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <CalendarIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {new Date(event.date).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <LocationIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {event.location}
                          </Typography>
                        </Box>
                        <Chip 
                          label={`${event.application_count || 0} Applications`} 
                          size="small" 
                          color="primary"
                          variant="outlined"
                        />
                      </CardContent>
                      <CardActions>
                        <Button 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            history.push(`/event/${event.event_id}/applications`);
                          }}
                        >
                          Manage Applications
                        </Button>
                        <Button 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            history.push(`/event/${event.event_id}`);
                          }}
                        >
                          View Details
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <EventIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.3 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No events yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Create your first event to start hosting vendors and growing your organization
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => history.push('/create-event')}
                  size="large"
                >
                  Create Your First Event
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Gallery Tab */}
        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Gallery</Typography>
              <Button
                variant="contained"
                startIcon={<AddPhotoIcon />}
                onClick={handleUploadClick}
              >
                Upload Image
              </Button>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileChange}
              />
            </Box>
            
            {profile?.gallery?.length > 0 ? (
              <ImageList cols={3} gap={8}>
                {profile.gallery.map((image, index) => (
                  <ImageListItem key={index}>
                    <img
                      src={image.url}
                      alt={image.caption || `Gallery image ${index + 1}`}
                      loading="lazy"
                    />
                    <ImageListItemBar
                      title={image.caption}
                      subtitle={image.date}
                      actionIcon={
                        <Box>
                          <IconButton
                            sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                            aria-label={`info about ${image.caption}`}
                          >
                            <MoreVertIcon />
                          </IconButton>
                          <IconButton
                            sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                            aria-label="delete image"
                            onClick={() => handleDeleteImage(image.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      }
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No images in your gallery yet. Upload some to showcase your events!
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Reviews Tab */}
        {activeTab === 3 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {profile?.reviews?.map((review, index) => (
                <Grid item xs={12} key={index}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar src={review.user_avatar} sx={{ mr: 2 }} />
                        <Box>
                          <Typography variant="subtitle1">
                            {review.user_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(review.date).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body1" paragraph>
                        {review.comment}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {[...Array(5)].map((_, i) => (
                          <StarIcon
                            key={i}
                            color={i < review.rating ? 'warning' : 'disabled'}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>
        </Box>
        )}
      </Paper>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)}>
        <DialogTitle>Upload Gallery Image</DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <img
                src={URL.createObjectURL(selectedImage)}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: '200px' }}
              />
            </Box>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Image Caption"
            fullWidth
            variant="outlined"
            value={imageCaption}
            onChange={(e) => setImageCaption(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleUploadConfirm} 
            variant="contained" 
            disabled={uploadProgress}
          >
            {uploadProgress ? <CircularProgress size={24} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Social Media Dialog */}
      <Dialog open={socialMediaDialogOpen} onClose={() => setSocialMediaDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Social Media Links</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="facebook-input">Facebook</InputLabel>
              <OutlinedInput
                id="facebook-input"
                value={socialMediaLinks.facebook}
                onChange={handleSocialMediaChange('facebook')}
                startAdornment={
                  <InputAdornment position="start">
                    <FacebookIcon />
                  </InputAdornment>
                }
                placeholder="https://facebook.com/yourpage"
                label="Facebook"
              />
            </FormControl>
            
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="twitter-input">Twitter</InputLabel>
              <OutlinedInput
                id="twitter-input"
                value={socialMediaLinks.twitter}
                onChange={handleSocialMediaChange('twitter')}
                startAdornment={
                  <InputAdornment position="start">
                    <TwitterIcon />
                  </InputAdornment>
                }
                placeholder="https://twitter.com/yourhandle"
                label="Twitter"
              />
            </FormControl>
            
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="instagram-input">Instagram</InputLabel>
              <OutlinedInput
                id="instagram-input"
                value={socialMediaLinks.instagram}
                onChange={handleSocialMediaChange('instagram')}
                startAdornment={
                  <InputAdornment position="start">
                    <InstagramIcon />
                  </InputAdornment>
                }
                placeholder="https://instagram.com/yourprofile"
                label="Instagram"
              />
            </FormControl>
            
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="linkedin-input">LinkedIn</InputLabel>
              <OutlinedInput
                id="linkedin-input"
                value={socialMediaLinks.linkedin}
                onChange={handleSocialMediaChange('linkedin')}
                startAdornment={
                  <InputAdornment position="start">
                    <LinkedInIcon />
                  </InputAdornment>
                }
                placeholder="https://linkedin.com/in/yourprofile"
                label="LinkedIn"
              />
            </FormControl>
      </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSocialMediaDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSocialMediaSave} 
            variant="contained"
            disabled={updateSocialMediaMutation.isLoading}
          >
            {updateSocialMediaMutation.isLoading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Container>
    </Box>
  );
};

export default OrganizerProfile;
